import { BookOpen, IndianRupee, Languages, Puzzle, Users } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';

import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import { MAX_SECTION_SEATS, type ClassFormValues } from '../schemas/class.schema';
// import { classColour, classIcon } from '../constants';
import { STAGE_TONE, stageOfClass, toAmount, totalIntake } from '../utils';

export interface ClassPreviewCardProps {
  form: UseFormReturn<ClassFormValues>;
  /** The session this class is being set up for, straight from the roster. */
  academicYear?: string;
}

/** One curriculum list, as a count and a row of chips. */
function CurriculumRow({
  icon: Icon,
  label,
  names,
  chipClass,
}: {
  icon: typeof BookOpen;
  label: string;
  names: string[];
  chipClass: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-slate-600 dark:text-slate-500">
        <Icon className="h-3 w-3" aria-hidden />
        {label}
        <span className="ml-auto font-mono text-slate-600 dark:text-slate-400 text-[12px]">{names.length}</span>
      </p>
      {names.length === 0 ? (
        <p className="text-[11px] font-medium text-slate-400 dark:text-slate-600">Nothing yet</p>
      ) : (
        <div className="flex flex-wrap gap-1">
          {names.map((name, index) => (
            <span
              key={`${name}-${index}`}
              className={cn(
                'animate-tab-fade text-slate-700 rounded-full border px-2 py-0.5 text-[11px] font-bold',
                chipClass,
              )}
            >
              {name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The class being typed, drawn as the card it will become.
 *
 * This is the setup screen's graphic, and it is a live one rather than an
 * illustration: an institute setting up Class 8 is really asking "what will this
 * look like once it exists", and every answer is already in the form. The seat
 * bars grow as the caps are typed, the stage badge re-tints as the name is
 * written, and the money at the bottom is the figure the office actually argues
 * about — a year's fee times every seat in the class.
 *
 * It reads the form rather than taking a dozen props, so nothing can be passed
 * in stale. Watching here rather than in the page also keeps the re-render on
 * every keystroke inside this card, away from the inputs being typed into.
 */
export function ClassPreviewCard({ form, academicYear }: ClassPreviewCardProps) {
  const values = form.watch();

  const name = (values.name ?? '').trim();
  const code = (values.code ?? '').trim().toUpperCase();
  // The category the institute stated, and only failing that the guess from the
  // name — the same rule the roster reads a saved class by.
  const stage = stageOfClass({ name, category: values.category || null });
  const tone = STAGE_TONE[stage];
  // const colour = classColour(values.colourKey);
  // const Icon = classIcon(values.iconKey);

  const sections = values.sections ?? [];
  const namedSections = sections.filter((section) => section.name.trim() !== '');
  const intake = totalIntake(sections);

  const minAge = (values.minAgeYears ?? '').trim();
  const maxAge = (values.maxAgeYears ?? '').trim();

  const tuition = toAmount(values.tuitionFee);
  const books = toAmount(values.booksFee);
  const other = toAmount(values.otherFees);
  const yearly = tuition + books + other;

  const listOf = (entries: { name: string }[] | undefined) =>
    (entries ?? []).map((entry) => entry.name.trim()).filter(Boolean);

  const core = listOf(values.coreSubjects);
  const electives = listOf(values.electives);
  const languages = listOf(values.languages);

  /**
   * The seat bars are drawn against the biggest section, not against the
   * product's 500-seat ceiling — three sections of 35 should read as three full
   * bars, not as three slivers.
   */
  const widest = Math.max(1, ...sections.map((section) => toAmount(section.maxSeats)));

  return (
    <div className="relative overflow-hidden rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-[#111c2e] shadow-[0_12px_32px_-8px_rgba(15,23,42,0.12)]">
      {/* The stage's own colour along the top, so the card changes character as
          the class name is typed. */}
      <div className={cn('h-1.5 w-full transition-colors duration-500', tone.bar)} aria-hidden />

      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-sheen dark:via-white/5" />
      </div>

      <div className="relative flex flex-col gap-5 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-widest text-blue-500 dark:text-blue-400">
              Live preview
            </p>
            {/* The mark the class is recognised by, in the colour chosen for it
                — the one part of the card that is a choice rather than a fact. */}
            {/* <span
              className={cn(
                'mt-2 grid h-10 w-10 place-items-center rounded-2xl transition-colors duration-300',
                colour.soft,
              )}
              aria-hidden
            >
              <Icon className="h-5 w-5" />
            </span> */}
            <h2
              className={cn(
                'mt-1 truncate text-xl font-black tracking-tight',
                name ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-700',
              )}
            >
              {name || 'Untitled class'}
            </h2>
            <p className="mt-1 text-[12px] font-bold text-slate-500 dark:text-slate-400">
              {code && (
                <span className="font-mono text-slate-600 dark:text-slate-300">{code} · </span>
              )}
              {minAge && maxAge ? `Ages ${minAge}–${maxAge}` : 'No age rule'}
              {academicYear && ` · Session ${academicYear}`}
            </p>
          </div>

          <span
            className={cn(
              'shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-wider transition-colors duration-500',
              tone.badge,
            )}
          >
            {stage}
          </span>
        </div>

        {/* Sections, each bar as wide as its cap is against the widest one. */}
        <div className="flex flex-col gap-2">
          <p className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">
            <Users className="h-3 w-3" aria-hidden />
            Sections
            <span className="ml-auto font-mono text-slate-600 dark:text-slate-400 text-[11px]">
              {intake.toLocaleString('en-IN')} seats
            </span>
          </p>

          {namedSections.length === 0 ? (
            <p className="text-[11px] font-medium text-slate-400 dark:text-slate-600">
              Name a section and it appears here.
            </p>
          ) : (
            <div className="flex flex-col gap-1.5">
              {namedSections.map((section, index) => {
                const cap = Math.min(toAmount(section.maxSeats), MAX_SECTION_SEATS);
                const floor = toAmount(section.minSeats);

                return (
                  <div key={index} className="flex items-center gap-2.5">
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-lg bg-blue-100 dark:bg-blue-950/60 text-[10px] font-black text-blue-700 dark:text-blue-300">
                      {section.name.trim().slice(0, 2).toUpperCase()}
                    </span>

                    <span className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                      {/* The one style this file computes rather than names: a
                          bar's width comes from what was typed, and there is no
                          utility class for a number that does not exist until
                          runtime. Everything else here is a token (§7). */}
                      <span
                        className={cn('block h-full rounded-full transition-all duration-500', tone.bar)}
                        style={{ width: `${Math.min(100, (cap / widest) * 100)}%` }}
                      />
                    </span>

                    <span className="shrink-0 font-mono text-[11px] font-bold text-slate-500 dark:text-slate-400">
                      {floor || '–'}–{cap || '–'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 border-t border-slate-100 dark:border-slate-800 pt-4">
          <CurriculumRow
            icon={BookOpen}
            label="Core"
            names={core}
            chipClass="border-blue-200 dark:border-blue-900/60 bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300"
          />
          <CurriculumRow
            icon={Puzzle}
            label="Electives"
            names={electives}
            chipClass="border-sky-200 dark:border-sky-900/60 bg-sky-50 dark:bg-sky-950/50 text-sky-700 dark:text-sky-300"
          />
          <CurriculumRow
            icon={Languages}
            label="Languages"
            names={languages}
            chipClass="border-sky-200 dark:border-sky-900/60 bg-sky-50 dark:bg-sky-950/50 text-sky-700 dark:text-sky-300"
          />
        </div>

        {/* What it costs a family, and what a full class is worth to the
            institute — the second is the figure fee planning is actually done
            against, and nothing else on the screen works it out. */}
        <div className="flex flex-col gap-1.5 rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 p-3.5">
          <p className="flex items-center gap-1.5 text-[12px] font-black uppercase tracking-wide text-slate-700 dark:text-slate-400">
            <IndianRupee className="h-3 w-3" aria-hidden />
            Fees a year
          </p>

          <dl className="flex flex-col gap-1 text-[11px] font-bold">
            <div className="flex items-center justify-between gap-2 pt-2">
              <dt className="text-slate-500 tracking-wide text-[12px] dark:text-slate-600">Tuition</dt>
              <dd className="font-mono text-slate-700 text-[13px] dark:text-slate-200">
                {formatCurrency(tuition)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-2">
              <dt className="text-slate-500 tracking-wide text-[12px] dark:text-slate-400">Books</dt>
              <dd className="font-mono text-slate-700 text-[13px] dark:text-slate-200">
                {formatCurrency(books)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-2">
              <dt className="text-slate-500 tracking-wide text-[12px] dark:text-slate-400">Other</dt>
              <dd className="font-mono text-slate-700 text-[13px] dark:text-slate-200">
                {formatCurrency(other)}
              </dd>
            </div>
            <div className="mt-1 flex items-center justify-between gap-2 border-t border-slate-200 dark:border-slate-800 pt-1.5">
              <dt className="font-black text-slate-700 text-[12px] dark:text-slate-200">Per student</dt>
              <dd className="font-mono text-[13px] font-black text-blue-700 dark:text-blue-300">
                {formatCurrency(yearly)}
              </dd>
            </div>
          </dl>

          {yearly > 0 && (
            <p className="mt-1 text-[10px] font-bold text-slate-400 dark:text-slate-500">
              ≈ {formatCurrency(Math.round(yearly / 12))} a month
              {intake > 0 && ` · ${formatCurrency(yearly * intake)} at full intake`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
